import { Component, OnDestroy, OnInit } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { ProjectTheme } from "src/app/Models/ProjectTheme";
import { SeoService, SITE_NAME, SITE_URL } from "src/app/Services/seo.service";

/** Wissal Univers showrooms, published to search engines as schema.org `Store` structured data */
const SHOWROOMS = [
  {
    city: "Sétif",
    region: "Sétif",
    street: "Coop Elmasaa Bérarma",
    postalCode: "19000",
    phones: ["+213 770 32 71 51", "+213 770 12 08 14"],
    map: "https://maps.app.goo.gl/CqSpsK3pPHckufZ69",
  },
  {
    city: "Constantine",
    region: "Constantine",
    street: "304, Cité 400 Logts UV 7 Nouvelle Ville, Ali Mendjli",
    phones: ["+213 770 81 00 19", "+213 770 11 34 47"],
    map: "https://maps.app.goo.gl/pj1hS2ffYHmJ6Gdf6",
  },
  {
    city: "Alger Beni Messous",
    locality: "Beni Messous",
    region: "Alger",
    phones: ["+213 770 39 54 66", "+213 770 39 54 29", "+213 770 32 01 86"],
  },
  {
    city: "Oran",
    region: "Oran",
    phones: ["+213 770 76 59 95", "+213 770 74 88 64"],
  },
  {
    city: "Mostaganem",
    region: "Mostaganem",
    phones: ["+213 770 84 75 66"],
  },
];

/** Store cards; `key` is the name and address under `homePage.stores.locations`. */
const STORE_LOCATIONS = [
  { key: "setif", link: "https://maps.app.goo.gl/CqSpsK3pPHckufZ69" },
  { key: "elEulma", link: "https://maps.app.goo.gl/A94LvPiX53PqYu7t9" },
  { key: "constantine", link: "https://maps.app.goo.gl/pj1hS2ffYHmJ6Gdf6" },
];

@Component({
  selector: "app-stores",
  standalone: false,
  templateUrl: "./stores.component.html",
  styleUrl: "./stores.component.scss",
})
export class StoresComponent implements OnInit, OnDestroy {
  theme: ProjectTheme = "WUnivers";
  imageSrc: string = "assets/images/WU_Stores.jpg";
  locations: { title: string; text: string; link: string; linkDescription: string }[];

  /** The page is re-created on language change (see MainComponent), so the current language is enough. */
  constructor(private seoService: SeoService, translate: TranslateService) {
    const linkDescription = translate.instant("homePage.stores.linkDescription");
    this.locations = STORE_LOCATIONS.map(({ key, link }) => ({
      title: translate.instant(`homePage.stores.locations.${key}.title`),
      text: translate.instant(`homePage.stores.locations.${key}.text`),
      link,
      linkDescription,
    }));
  }

  ngOnInit(): void {
    this.seoService.setJsonLd("stores", {
      "@context": "https://schema.org",
      "@graph": SHOWROOMS.map((showroom) => ({
        "@type": "Store",
        name: `${SITE_NAME} ${showroom.city}`,
        url: `${SITE_URL}/stores`,
        telephone: showroom.phones[0],
        contactPoint: showroom.phones.map((telephone) => ({
          "@type": "ContactPoint",
          telephone,
          contactType: "customer service",
        })),
        address: {
          "@type": "PostalAddress",
          ...(showroom.street && { streetAddress: showroom.street }),
          ...(showroom.postalCode && { postalCode: showroom.postalCode }),
          addressLocality: showroom.locality ?? showroom.city,
          addressRegion: showroom.region,
          addressCountry: "DZ",
        },
        ...(showroom.map && { hasMap: showroom.map }),
        parentOrganization: { "@type": "Organization", name: SITE_NAME, url: SITE_URL },
      })),
    });
  }

  ngOnDestroy(): void {
    this.seoService.removeJsonLd("stores");
  }
}
